// The readiness ladder's RUNG VOCABULARY — the ordered ids `next_steps` and
// `GET /v1/next-steps` publish, and the one place they are spelled.
//
// ── THE DEFECT ───────────────────────────────────────────────────────────────
// The ladder is served by `packages/mcp` (`resolveReadiness`, `tools.ts`), whose
// `NextStepIdSchema` was module-private, and rendered by the dashboard's
// `ReadinessLadder`, which builds its label key DYNAMICALLY —
// `t(\`readiness.step_${rung.step}\`)`. `apps/dashboard/src/lib/readiness.ts`
// therefore RESTATED the vocabulary as `READINESS_STEPS`, because
// `@developerz/mcp` is not a dashboard dependency and exporting the schema from
// there would pull the whole MCP tool registry into a CDN-served SPA bundle.
//
// A restated enum plus a dynamic key is a rung that renders as its own key. From
// #3632 until arc 101 slice 05 it did exactly that: the route grew
// `box_attached` and `mcp_connected`, the array did not, and a live screen
// showed `⟦readiness.step_box_attached⟧`. `plan_selected` (#4611) repeated it.
// The label-coverage test could not see either, because it grades the route
// against THAT SAME ARRAY — a copy checked against itself.
//
// ── WHY HERE, AND WHY NOT A GUARD ────────────────────────────────────────────
// This is a MOVE, not a check. `@developerz/domain` is already a dependency of
// both ends, imports nothing internal, and is browser-evaluable
// (`browser-evaluability.test.ts`), so it is the one address both a Hono server
// and a SolidJS SPA can hold. `plan_selected`, `box_attached` and
// `mcp_connected` are spelled below as `OnboardingState` members rather than as
// strings, which is the other half of why they belong beside the funnel rather
// than in a tool file.
//
// A cross-package TEXT guard was the alternative and is deliberately refused:
// the schema is built from CONSTANT REFERENCES (`OnboardingState.McpConnected`,
// not `'mcp_connected'`), so a source-text scan breaks on a harmless refactor
// and passes over the real defect. Worse than none.
//
// With the vocabulary here, a rung the dashboard has no label for is a RED TEST:
// `apps/dashboard/src/components/RepoReadiness.test.tsx` imports THIS array and
// grades `apps/dashboard/locales/en.json` against it, so the assertion is no
// longer a copy checked against itself. Measured 2026-09-20 — append an id to
// `READINESS_STEPS` and that test fails with `readiness.step_<id>` /
// `Received: undefined`; to re-derive, plant one and run
// `bun run test apps/dashboard/src/components/RepoReadiness.test.tsx`.
//
// It is NOT a type error, and there is no label map for one to land on:
// `ReadinessLadder.tsx` builds `t(`readiness.step_${rung.step}`)` and `t()`
// takes a `string` (`Translator`, `packages/i18n/src/translator.ts`), so a key
// built from a template literal is invisible to TypeScript. The same plant
// leaves `scripts/typecheck-scoped.ts` green over both files.
//
// ── THE ORDER IS PART OF THE CONTRACT ────────────────────────────────────────
// DEPENDENCY order, not severity: a box cannot run without a key, a repo cannot
// be enabled before the App can see it, and none of it matters while the
// credential is capped. `next` is the first BLOCKING rung, so the order is what
// makes it actionable — an agent clears one rung and calls again. The renderer
// renders the order it is served and never re-sorts.

import { z } from 'zod';
import { OnboardingState } from '../entities/account';

/**
 * The rung a `no_scout_capacity` refusal points a caller at when the gap is on
 * the MACHINE rather than in the fleet's size (`SCOUT_GAP_RUNG`,
 * `packages/mcp/src/tools.ts`).
 *
 * Named rather than inlined because it is read in two directions — the ladder
 * builds the rung with it and the refusal sentence names it — and a refusal that
 * sends a caller to a rung the ladder does not serve is worse than one that
 * names none.
 */
export const SCOUT_BOX_STEP = 'scout_box';

/**
 * The MAINTENANCE rungs (#7152) — "is this project fully AI-maintained", asked
 * of one repo AFTER the ladder below has answered "can I start". They are the
 * tail of {@link READINESS_STEPS}, never a second vocabulary: one alphabet, so
 * the dashboard's label test grades these against `en.json` exactly as it does
 * the onboarding rungs. They are SERVED apart — `next_steps`' `maintenance`
 * block, never its `steps` — because every one of them is `blocked` on a repo
 * the day it is onboarded, and folding them into `steps` would hold `ready`
 * false for every repo there is: `get_started` loops until `ready: true`.
 *
 * Each is a pure read over rows already stored, in dependency order: a red
 * base makes every later rung moot, the scorecard is the terrain the agents
 * work on, and the policy-driven rungs come after the repo can carry them. The
 * top of the ladder — "developerz.ai is this project's maintainer" — is NOT a
 * rung: it is `fully_maintained`, derived when every rung holds, because there
 * is no action that earns it, only a state.
 *
 * STORED, PUBLIC IDS — a rename is a new id. `repo_` prefix: each is a fact
 * about the ONE repo the read named, like `repo_onboarded` / `repo_secrets`.
 */
export const MAINTENANCE_RUNGS = [
  // No open base-red check, base-gate red streak, deploy red or repo env fault on the
  // repo's ENABLED row, matched by repo id.
  'repo_base_green',
  // The stored onboarding scorecard (`ai_first_score`) — every item `ok`.
  'repo_scorecard',
  // The active policy sets the issue stale sweep and leaves inactive-close on.
  'repo_backlog_hygiene',
  // The active policy declares `schedules:`, and each one has a `schedule.fired` row
  // inside one week of slots.
  'repo_checkups',
  // A connected `sentry` inbound source has DELIVERED for this repo recently — an ask it
  // filed bound to the repo; `connected` alone is the source's initial state, not proof.
  'repo_error_source',
  // The platform can propose a CI change and has: the repo's App installation carries
  // `workflows: write` in its STORED permission map (`installations.permissions`; null =
  // never observed = not proven), and the one-time `ci_hardening` setup ask is delivered,
  // or the last onboarding pass assessed the workflows `clean`. Such a change merges only
  // on the gate-path transform proof (plan 101-maintenance-one-way-doors, door 3).
  'repo_ci_gate',
] as const;

/**
 * The rungs, in the order a first-run agent must clear them, ending at the
 * capability the whole ladder is FOR (`plan_start`), then the
 * {@link MAINTENANCE_RUNGS} tail.
 *
 * `plan_selected`, `box_attached` and `mcp_connected` are `OnboardingState`
 * members BY REFERENCE, not by spelling: the wizard's step, the funnel row and
 * this rung are ONE step named ONE way. A rename of the funnel state therefore
 * renames the rung, rather than leaving a ladder pointing at a state that no
 * longer exists. `packages/mcp/src/onboarding-guidance.test.ts` pins
 * `box_attached` and `mcp_connected` against `OnboardingStateSchema.options`,
 * and holds their ladder positions to the funnel's own order; `plan_selected`
 * is pinned by ladder position alone (#4611), not against the enum.
 */
export const READINESS_STEPS = [
  'platform_access',
  'setup_scopes',
  // The funnel's plan pick (#4611), before the key it is spent on — `authed ->
  // plan_selected -> key_set` is the funnel's own order, and it is a real
  // dependency: a BYOK key, a box and a dispatch are all spent against capacity
  // this account may not have bought yet.
  OnboardingState.PlanSelected,
  'byok_org_key',
  'github_app_install',
  // Is there a live enrolment at all (#3632). Mirrors the funnel state of the
  // same name, so the wizard's "attach a server" step, the funnel row and this
  // rung are one step.
  OnboardingState.BoxAttached,
  'repo_enabled',
  SCOUT_BOX_STEP,
  /** The next two are served ONLY when the read named a repo. */
  'repo_onboarded',
  'repo_secrets',
  // The funnel's last step (#3632): a chat client holds a credential.
  OnboardingState.McpConnected,
  'queue',
  ...MAINTENANCE_RUNGS,
] as const;

/** One rung's id. The alphabet both doors publish and every renderer must label. */
export const ReadinessStepIdSchema = z.enum(READINESS_STEPS);
export type ReadinessStepId = z.infer<typeof ReadinessStepIdSchema>;
